import { Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import {
  IndividualEntityDividerComponent
} from "@pages/individuals/components/individual-profile-page/components/individual-entity-divider/individual-entity-divider.component";
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { ContactsApiService } from "@shared/services/contacts-api.service";
import { ContactsService } from "@shared/services/contacts.service";
import { getCommonActionCellColDef } from "@shared/utils/grid/get-common-column-chooser-params";
import { ColDef, IsFullWidthRowParams } from "ag-grid-community";
import { GridOptions, RowHeightParams } from "ag-grid-enterprise";
import { ToastrService } from "ngx-toastr";
import { Observable, of, switchMap, tap } from "rxjs";
import { Contact } from "@models/common/contact";
import { IndividualDto } from '@models/individuals/individual-dto';
import { CommonOffcanvasComponent } from "@shared/components/commons/common-offcanvas/common-offcanvas.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import {
  AgIndividualContactActionsComponent
} from "./components/ag-individual-contact-actions/ag-individual-contact-actions.component";
import { IEditContactModalResult } from "@shared/components/contacts/edit-contact-modal/edit-contact-modal.component";
import { getContactsColDefs, getContactsOverlayNoRowsTemplate } from "./utils/get-contacts-col-defs";

export interface IContactsChangeEvent {
  contacts: Contact[];
  primaryContact: Contact | null;
}

@Component({
  selector: 'app-contacts',
  standalone: true,
  imports: [
    CommonOffcanvasComponent,
    LoaderComponent,
    ListGridClientSideComponent,
  ],
  templateUrl: 'contacts.component.html',
  styleUrl: 'contacts.component.scss'
})
export class ContactsComponent extends CommonOffcanvasComponent {
  private contactsService = inject(ContactsService);
  private contactsApiService = inject(ContactsApiService);
  private toastr = inject(ToastrService);

  @Input('individual') set individualSetter(individual: IndividualDto) {
    this.individual = individual;
    this.fillViewContacts(individual);
  }

  @Output() contactsChanged = new EventEmitter<IContactsChangeEvent>();

  isLoading = signal<boolean>(false);
  viewContacts = signal<(Contact | string)[]>([]);
  individual: IndividualDto | null = null;
  colDefs: ColDef[] = [];
  contactsRowSelection = BaseListGridComponent.noCheckboxesRowSelection;

  loadContacts(): void {
    if (!this.individual) return;

    this.isLoading.set(true);
    this.contactsApiService.getIndividualContacts(this.individual.individualId)
      .subscribe({
        next: (contacts) => {
          this.individual!.contacts = contacts;
          this.fillViewContacts(this.individual!);
          this.contactsChanged.emit({ contacts, primaryContact: this.individual?.primaryContact ?? null });
          this.isLoading.set(false);
        },
        error: (error) => {
          console.warn('[ContactsComponent.loadContacts] error: ', error);
          this.toastr.error('Failed to load individual contacts', 'Error');
        }
      });
  }

  fillViewContacts(individual: IndividualDto): void {
    const contacts: (Contact | string)[] = [];

    if (!individual.primaryContact) {
      this.viewContacts.set(individual.contacts);
      this.setColDefs();
      this.isLoading.set(false);
      return;
    }

    const otherContacts = individual.contacts
      .filter((contact) => contact.contactId !== individual.primaryContactId);

    contacts.push('Primary Contact');
    contacts.push(individual.primaryContact);

    if (individual.contacts.length > 1) {
      contacts.push('Other Contacts');
      contacts.push(...otherContacts);
    }

    this.setColDefs();
    this.viewContacts.set(contacts);
    this.isLoading.set(false);
  }

  openEditContactModal(contact: Contact | null = null): void {
    const { instance, result } = this.contactsService.openEditContactModal();

    instance.individual = this.individual!;
    instance.contact = contact;

    result.then(
      (result: IEditContactModalResult) => {
        if (result.isPrimaryContact && this.individual) {
          this.individual.primaryContactId = result.contact.contactId;
          this.individual.primaryContact = result.contact;
        }

        this.loadContacts();
      },
      () => {
      }
    );
  }

  onAddContact(): void {
    this.openEditContactModal();
  }

  editContact(contact: Contact): void {
    this.openEditContactModal(contact);
  }

  setContactAsPrimary(contact: Contact): Observable<unknown> {
    this.isLoading.set(true);
    return this.contactsApiService.setIndividualPrimaryContact(this.individual!.individualId, contact.contactId)
      .pipe(
        tap({
          next: () => {
            if (this.individual) {
              this.individual.primaryContactId = contact.contactId;
              this.individual.primaryContact = contact;
              this.fillViewContacts(this.individual);
            }

            this.isLoading.set(false);
          },
          error: (error) => {
            console.warn('[ContactsComponent.setContactAsPrimary] error: ', error);
          }
        })
      );
  }

  deleteContact(contact: Contact): void {
    if (!confirm('Are you sure you want to delete this contact?')) return;

    this.contactsApiService.deleteContact(contact.contactId)
      .pipe(
        switchMap(() => {
          if (this.individual?.primaryContactId && this.individual?.primaryContactId == contact.contactId) {
            this.individual.primaryContactId = null;
            this.individual.primaryContact = null;
            const newPrimaryContact = this.viewContacts()
              .find((c) => typeof c !== "string" && c.contactId !== contact.contactId) as Contact | undefined;

            if (newPrimaryContact) {
              return this.setContactAsPrimary(newPrimaryContact);
            }
          }

          return of(null);
        })
      )
      .subscribe({
        next: () => this.loadContacts(),
        error: (error) => {
          console.warn('[ContactsComponent.deleteContact] error: ', error);
          this.loadContacts();
        }
      });
  }

  closeContacts(): void {
    this.closeOffcanvas();
  }

  // ===== ===== ===== Table Columns config and other ===== ===== =====

  readonly fullWidthCellRenderer = IndividualEntityDividerComponent;
  readonly gridOptions: GridOptions = { overlayNoRowsTemplate: getContactsOverlayNoRowsTemplate() };

  setColDefs(): void {
    const colDefs = getContactsColDefs(false);
    const actionColDef: ColDef = {
      ...getCommonActionCellColDef(colDefs),
      headerName: '',
      minWidth: 64,
      width: 64,
      cellRenderer: AgIndividualContactActionsComponent,
      cellRendererParams: {
        primaryContactId: this.individual?.primaryContactId ?? null,
        onSetContactAsPrimary: ((contact: Contact) => this.setContactAsPrimary(contact).subscribe()).bind(this),
        onEditContact: this.editContact.bind(this),
        onDeleteContact: this.deleteContact.bind(this),
      }
    };
    colDefs.push(actionColDef);

    this.colDefs = colDefs;
  }

  isFullWidthRow(params: IsFullWidthRowParams<unknown>): boolean {
    return typeof params.rowNode.data == 'string';
  }

  getRowHeight: (params: RowHeightParams) => number | undefined | null = (
    params: RowHeightParams,
  ) => {
    return typeof params.data == 'string'
      ? 24
      : 56;
  };
}
